import {
    UnitAvailability,
    isMinStayMet,
    getAvailabilityObject,
    hasEmptyAvailability
} from '@homeaway/property-availability-parser';
import {isEqual, isPast, isBefore, isInRange, areBothDatesValid} from './dateHelper';
import {isDateError} from './errorEvents';
import differenceInDays from 'date-fns/differenceInDays';

function listingIsAvailable(arriveDateText, departDateText, availabilityData) {
    const checkinObj = availabilityData.checkin[arriveDateText] || {};
    const checkoutObj = availabilityData.checkout[departDateText] || {};
    return (
        checkinObj &&
        checkoutObj &&
        !checkinObj.booked &&
        !checkoutObj.previousDayBooked &&
        !checkoutObj.unfocused
    );
}

function areDatesEnabled(arriveDateText, departDateText, availabilityData) {
    const checkinObj = availabilityData.checkin[arriveDateText] || {};
    const checkoutObj = availabilityData.checkout[departDateText] || {};
    return checkinObj && checkoutObj && !checkinObj.disabled && !checkoutObj.disabled;
}

function hasMinStayError(arriveDateText, departDateText, availabilityData, defaultMinStay) {
    const checkinObj = availabilityData.checkin[arriveDateText] || {};
    const checkoutObj = availabilityData.checkout[departDateText] || {
        minStayMet: isMinStayMet(
            differenceInDays(new Date(departDateText), new Date(arriveDateText)),
            defaultMinStay
        )
    };

    return checkinObj && checkoutObj && !checkoutObj.minStayMet;
}

function hasMinPriorNotifyError(arriveDateText, availabilityData, currentDate, ua) {
    const beginDate = ua.getBeginDate();
    const beginDateOffsetFromToday = differenceInDays(
        new Date(currentDate),
        new Date(beginDate.text)
    );
    const dateIndex = ua.length;
    const checkinObj = availabilityData.checkin[arriveDateText] || {
        minPriorNotifyMet: ua.minPriorNotifyMet(dateIndex, beginDateOffsetFromToday)
    };

    return checkinObj && !checkinObj.minPriorNotifyMet;
}

function hasSameDayArrivalError(currentDate, arriveDateText, isSameDayThrottleOn) {
    return !isSameDayThrottleOn && isEqual(currentDate, arriveDateText);
}

function hasDates({arriveDateText = null, departDateText = null}) {
    return !!arriveDateText && !!departDateText;
}

function areDatesInsideAvailabilityWindow(
    arriveDateText,
    departDateText,
    availabilityDateRange = {}
) {
    const {beginDate, endDate} = availabilityDateRange;

    return (
        isInRange(arriveDateText, beginDate, endDate) &&
        isInRange(departDateText, beginDate, endDate)
    );
}

/* eslint-disable complexity */
function hasValidDates({
    availabilityData,
    currentDate = null,
    arriveDateText = null,
    departDateText = null,
    availabilityCalendar = {},
    error = {}, // most likely a quote error
    skipAvailabilityValidation = false,
    isSameDayThrottleOn = false
}) {
    const availabilityObject = getAvailabilityObject(availabilityCalendar);
    const ua = new UnitAvailability(availabilityObject);
    const bothDatesExist = hasDates({arriveDateText, departDateText});
    const bothDatesValidDates = areBothDatesValid(arriveDateText, departDateText);
    const datesOutsideAvailabilityWindow = !areDatesInsideAvailabilityWindow(
        arriveDateText,
        departDateText,
        availabilityObject.dateRange
    );
    const arrivalDateInPast = isPast(arriveDateText);
    const unorderedDates = !isBefore(arriveDateText, departDateText);
    const formattingError = !bothDatesValidDates || arrivalDateInPast || unorderedDates;
    const isEmptyAvailability = hasEmptyAvailability(availabilityObject);

    let errorObj = {
        datesValid: false,
        datesEmpty: !bothDatesExist,
        hasMinStayError: null,
        hasSameDayArrivalError: null,
        hasUnavailableDates: null,
        hasFormattingError: formattingError
    };

    if (!bothDatesExist || formattingError) {
        return errorObj;
    }

    if (isEmptyAvailability || skipAvailabilityValidation) {
        errorObj.datesValid = true;
        return errorObj;
    }

    if (bothDatesExist && datesOutsideAvailabilityWindow) {
        errorObj.datesValid = false;
        return errorObj;
    }

    const datesAvailable = listingIsAvailable(arriveDateText, departDateText, availabilityData);
    const datesEnabled = areDatesEnabled(arriveDateText, departDateText, availabilityData);

    const minStayError = hasMinStayError(
        arriveDateText,
        departDateText,
        availabilityData,
        availabilityCalendar.availability.minStayDefault
    );
    const minPriorNotifyError = hasMinPriorNotifyError(
        arriveDateText,
        availabilityData,
        currentDate,
        ua
    );
    const sameDayArrivalError = hasSameDayArrivalError(
        currentDate,
        arriveDateText,
        isSameDayThrottleOn
    );
    const invalidDates = isDateError(error);

    const datesValid =
        datesAvailable &&
        datesEnabled &&
        !minStayError &&
        !sameDayArrivalError &&
        !invalidDates &&
        !minPriorNotifyError;

    return {
        datesValid,
        datesEmpty: !bothDatesExist,
        hasMinStayError: minStayError,
        hasMinPriorNotifyError: minPriorNotifyError,
        hasSameDayArrivalError: sameDayArrivalError,
        hasUnavailableDates: !datesAvailable,
        hasFormattingError: formattingError
    };
}
/* eslint-enable complexity */

export default listingIsAvailable;
export {
    hasValidDates,
    hasDates,
    listingIsAvailable,
    areDatesEnabled,
    hasMinStayError,
    hasMinPriorNotifyError,
    hasSameDayArrivalError
};
